import { Component, ElementRef, EventEmitter, OnDestroy, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SelectItem } from 'primeng/api';
import { MultiSelect } from 'primeng/multiselect';
import { forkJoin, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { AccountComponent } from 'src/app/account/account.component';
import { InfoReportService } from 'src/app/services/info-report.service';
import { LoginService } from 'src/app/services/login.service';
import { NESSValidators } from 'src/app/shared/validators/ness.validators';
import { AuthService } from '../../../auth/auth.service';
import { AuthRoles } from '../../../auth/auth.types';


@Component({
  selector: 'ness-tracking',
  templateUrl: './tracking.component.html'
})
export class TrackingComponent implements OnDestroy {
  @Output() tdSubmitted = new EventEmitter<any>();
  cgicPersonnel: SelectItem[] = [];
  priorities: ServerModels.ENESS_REF_IntelligenceReportPriorities[] = [];
  purposes: ServerModels.ENESS_REF_IntelligenceReportPurposes[] = [];
  referralGroups: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups[] = [];
  statuses: ServerModels.ENESS_REF_IntelligenceReportStatuses[] = [];
  fieldDivisions$: Observable<ServerModels.ENESS_REF_FieldDivisions[]> = this.loginService.fieldDivisions$;
  filteredReferralGroupsForCaseAssist: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups[] = [];
  filteredReferralGroupsForReferral: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups[] = [];
  trackingData: any;
  trackingDataForm: FormGroup;
  types: ServerModels.ENESS_REF_IntelligenceReportTypes[] = [];

  private _infoReporUpdatedSub$: Subscription;
  private _unsubscribe$: Subject<void> = new Subject<void>();

  private _cgicCode: string;
  set cgicCode(code: string) {
    this._cgicCode = code;
  }

  get cgicCode(): string {
    return this._cgicCode;
  }

  get ENESS_XREF_IRsAssignedCGICPersonnel(): FormArray {
    return this.trackingDataForm.get('ENESS_XREF_IRsAssignedCGICPersonnel') as FormArray;
  }

  public canEditFD: AuthRoles[] = ['NESS Administrator'];

  get ENESS_XREF_IRsPurposes(): FormArray {
    return this.trackingDataForm.get('ENESS_XREF_IRsPurposes') as FormArray;
  }

  get ENESS_XREF_IRsTypes(): FormArray {
    return this.trackingDataForm.get('ENESS_XREF_IRsTypes') as FormArray;
  }

  constructor(private fb: FormBuilder,
    private infoReportService: InfoReportService,
    private loginService: LoginService,
    private authService: AuthService,
    public elementRef: ElementRef) {
    this._cgicCode = this.authService.hasRole(this.canEditFD) ? '' : AccountComponent.userInfo?.Agency != null ? AccountComponent.userInfo.Agency.ENESS_REF_FieldDivisions.ENESS_REF_NIBINCGICCodes.OrganizationCode : AccountComponent.userInfo.FieldDivision.ENESS_REF_NIBINCGICCodes.OrganizationCode;
    this.trackingDataForm = this.fb.group({
      IntelligenceReportID: '',
      IntelligenceTrackingNumber: [{ value: '', disabled: true }],
      Title: ['', Validators.required],
      CGICCode: [{ value: this.cgicCode || '', disabled: !this.authService.hasRole(this.canEditFD) }, Validators.required],
      PriorityID: ['', Validators.required],
      StatusID: ['', Validators.required],
      ENESS_XREF_IRsAssignedCGICPersonnel: this.fb.array([], Validators.required),
      ENESS_XREF_IRsTypes: this.fb.array([], NESSValidators.multipleCheckboxRequireOne),
      ENESS_XREF_IRsPurposes: this.fb.array([], NESSValidators.multipleCheckboxRequireOne)
    });

    this._infoReporUpdatedSub$ = this.infoReportService.infoReportUpdated$.subscribe((updatedIR: ServerModels.ENESS_IntelligenceReports) => this._updateFormData(updatedIR));
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this._resetPersonnelFieldDivision();
    }, 0);
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this._infoReporUpdatedSub$.unsubscribe();
  }

  private _updateFormData(ir: ServerModels.ENESS_IntelligenceReports) {
    if (this.trackingDataForm) {
      forkJoin({
        cgicPersonnel: this.infoReportService.getCGICPersonnelIncludeAgency(!ir.CGICCode ? this.cgicCode : ir.CGICCode),
        cipStatus: this.infoReportService.getInformationReportStatuses(),
        cipPriority: this.infoReportService.getInformationReportPriorites(),
        cipTypes: this.infoReportService.getInformationReportTypes(),
        cipPurposes: this.infoReportService.getInformationReportPurposes(),
        cipReferralGroups: this.infoReportService.getATFReferralGroups()
      }).pipe(takeUntil(this._unsubscribe$)).subscribe({
        next: (val: { cgicPersonnel: ServerModels.CGICPersonnelViewModel[], cipStatus: ServerModels.ENESS_REF_IntelligenceReportStatuses[], cipPriority: ServerModels.ENESS_REF_IntelligenceReportPriorities[], cipTypes: ServerModels.ENESS_REF_IntelligenceReportTypes[], cipPurposes: ServerModels.ENESS_REF_IntelligenceReportPurposes[], cipReferralGroups: ServerModels.ENESS_REF_IntelligenceReportATFReferralGroups[] }) => {
          val.cipTypes.forEach(function (val: ServerModels.ENESS_REF_IntelligenceReportTypes, ind:number) {
            let idx: number = ir.ENESS_XREF_IRsTypes.findIndex(i => i.TypeID == val.TypeID);
            this.trackingDataForm.get('ENESS_XREF_IRsTypes').push(this.fb.group({
              chbx: idx > -1,
              IRTypeID: idx > -1 ? ir.ENESS_XREF_IRsTypes[idx].IRTypeID : null,
              IntelligenceReportID: idx > -1 ? ir.ENESS_XREF_IRsTypes[idx].IntelligenceReportID : null,
              TypeID: val.TypeID,
              OtherText: [{ value: (idx > -1 ? ir.ENESS_XREF_IRsTypes[idx].OtherText : ''), disabled: idx == -1 }, (val.Type == 'Other' ? [Validators.required, Validators.maxLength(80)] : null)]
            }))
          }, this);
          this.types = val.cipTypes;
          this.priorities = val.cipPriority;
          this.purposes = val.cipPurposes;
          this.statuses = val.cipStatus;
          this.referralGroups = val.cipReferralGroups;

          val.cipPurposes.forEach(function (val: ServerModels.ENESS_REF_IntelligenceReportPurposes, ind:number) {
            let idx: number = ir.ENESS_XREF_IRsPurposes.findIndex(i => i.PurposeID == val.PurposeID);

            if (idx > -1 && val.Purpose != 'External LE Case Assist/NIBIN Notification') {
              this.filterReferralGroup(ir.ENESS_XREF_IRsPurposes[idx].ENESS_XREF_IRsPurposesFieldDivisions.FieldDivisionID, val.PurposeID)
            }

            this.trackingDataForm.get('ENESS_XREF_IRsPurposes').push(this.fb.group({
              chbx: idx > -1,
              PurposeID: val.PurposeID,
              IRPurposeID: idx > -1 ? ir.ENESS_XREF_IRsPurposes[idx].IRPurposeID : null,
              IntelligenceReportID: idx > -1 ? ir.ENESS_XREF_IRsPurposes[idx].IntelligenceReportID : null,
              IRPurposeFieldDivisionID: idx > -1 ? ir.ENESS_XREF_IRsPurposes[idx].IRPurposeFieldDivisionID : null,
              ENESS_XREF_IRsPurposesFieldDivisions: this.fb.group({
                IRPurposeFieldDivisionID: idx > -1 && val.Purpose != 'External LE Case Assist/NIBIN Notification' ? ir.ENESS_XREF_IRsPurposes[idx].IRPurposeFieldDivisionID : null,
                FieldDivisionID: [{ value: (idx > -1 && val.Purpose != 'External LE Case Assist/NIBIN Notification' ? ir.ENESS_XREF_IRsPurposes[idx].ENESS_XREF_IRsPurposesFieldDivisions.FieldDivisionID : ''), disabled: idx == -1 }, (val.Purpose == 'External LE Case Assist/NIBIN Notification' ? null : Validators.required)],
                IntelligenceReportATFReferralGroupsID: [{ value: (idx > -1 && val.Purpose != 'External LE Case Assist/NIBIN Notification' ? ir.ENESS_XREF_IRsPurposes[idx].ENESS_XREF_IRsPurposesFieldDivisions.IntelligenceReportATFReferralGroupsID : ''), disabled: idx == -1 }, (val.Purpose == 'External LE Case Assist/NIBIN Notification' ? null : Validators.required)],
                NFORCECaseNumber: [{ value: (idx > -1 && val.Purpose != 'External LE Case Assist/NIBIN Notification' ? ir.ENESS_XREF_IRsPurposes[idx].ENESS_XREF_IRsPurposesFieldDivisions.NFORCECaseNumber : ''), disabled: idx == -1 }, (val.Purpose == 'External LE Case Assist/NIBIN Notification' ? null : (val.Purpose == 'ATF Case Assist' ? [Validators.required, NESSValidators.NFORCENumberValidator] : [NESSValidators.NFORCENumberValidator]))],
              })
            }));
          }, this);

          this.cgicPersonnel = val.cgicPersonnel?.map((p: ServerModels.CGICPersonnelViewModel) => { return { label: `${p.LastName}, ${p.FirstName} (${p.Email})`, value: p } });
        }
      });

      ir.ENESS_XREF_IRsAssignedCGICPersonnel.forEach(cp => {
        (<FormArray>this.trackingDataForm.get('ENESS_XREF_IRsAssignedCGICPersonnel')).push(this.fb.group({
          UserID: cp.UserID,
          IsPrimary: cp.IsPrimary,
          IRAssignedCGICPersonnelID: cp.IRAssignedCGICPersonnelID,
          IntelligenceReportID: cp.IntelligenceReportID,
          ENESS_ApplicationUser: this.fb.group({
            FirstName: cp.ENESS_ApplicationUser.FirstName,
            LastName: cp.ENESS_ApplicationUser.LastName,
            Email: cp.ENESS_ApplicationUser.Email
          })
        }));
      });

      this.trackingDataForm.patchValue({
        IntelligenceReportID: ir.IntelligenceReportID,
        IntelligenceTrackingNumber: ir.IntelligenceTrackingNumber,
        Title: ir.Title,
        CGICCode: ir.CGICCode == null ? this.cgicCode : ir.CGICCode,
        PriorityID: ir.PriorityID,
        StatusID: ir.StatusID
      });

      this.trackingDataForm.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
  }

  private _resetPersonnelFieldDivision(): void {
    let personnelFDEl: HTMLSelectElement = <HTMLSelectElement>this.elementRef.nativeElement.querySelector('#PersonnelCGICCode');
    if (personnelFDEl) {
      personnelFDEl.value = this._cgicCode || '';
    }
  }

  addAssignedPersonnel(evt:Event, ctrl: MultiSelect) {
    if (!ctrl.value) {
      return;
    }
    const values: ServerModels.CGICPersonnelViewModel[] = ctrl.value;
    values.forEach(x => {
      (<FormArray>this.trackingDataForm.get('ENESS_XREF_IRsAssignedCGICPersonnel')).push(this.fb.group({
        UserID: x.Id,
        IsPrimary: false,
        ENESS_ApplicationUser: this.fb.group({
          FirstName: x.FirstName,
          LastName: x.LastName,
          Email: x.Email
        })
      }));
    });
    ctrl.writeValue(null);
    this._resetPersonnelFieldDivision();
  }

  onSelectPrimaryPersonnel(evt: Event, userId: string) {
    (<FormArray>this.trackingDataForm.get("ENESS_XREF_IRsAssignedCGICPersonnel")).controls.forEach((x: AbstractControl) => {
      let isChecked = x.get('UserID').value == userId;
      x.get('IsPrimary').setValue(isChecked);
    });
  }

  onPurposeChange(evt:Event, index:number) {
    let fa = this.trackingDataForm.get('ENESS_XREF_IRsPurposes') as FormArray;
    if (evt['target']['checked']) {
      fa.at(index).get('ENESS_XREF_IRsPurposesFieldDivisions.NFORCECaseNumber').enable();
      fa.at(index).get('ENESS_XREF_IRsPurposesFieldDivisions.IntelligenceReportATFReferralGroupsID').disable();
      fa.at(index).get('ENESS_XREF_IRsPurposesFieldDivisions.FieldDivisionID').enable();
    } else {
      fa.at(index).get('ENESS_XREF_IRsPurposesFieldDivisions.NFORCECaseNumber').setValue('');
      fa.at(index).get('ENESS_XREF_IRsPurposesFieldDivisions.NFORCECaseNumber').updateValueAndValidity();
      fa.at(index).get('ENESS_XREF_IRsPurposesFieldDivisions.IntelligenceReportATFReferralGroupsID').setValue('');
      fa.at(index).get('ENESS_XREF_IRsPurposesFieldDivisions.FieldDivisionID').setValue('');
      fa.at(index).get('ENESS_XREF_IRsPurposesFieldDivisions.FieldDivisionID').updateValueAndValidity();

      fa.at(index).get('ENESS_XREF_IRsPurposesFieldDivisions.NFORCECaseNumber').disable();
      fa.at(index).get('ENESS_XREF_IRsPurposesFieldDivisions.IntelligenceReportATFReferralGroupsID').disable();
      fa.at(index).get('ENESS_XREF_IRsPurposesFieldDivisions.FieldDivisionID').disable();
    }
  }

  onDivisionChange(event:Event, index:number) {
    let fa = this.trackingDataForm.get('ENESS_XREF_IRsPurposes') as FormArray;
    let fieldDivision = fa.at(index).get('ENESS_XREF_IRsPurposesFieldDivisions.FieldDivisionID').value;
    let purposes = fa.at(index).get('PurposeID').value;
    if (purposes != null) {
      this.filterReferralGroup(fieldDivision, purposes);
    }
    fa.at(index).get('ENESS_XREF_IRsPurposesFieldDivisions.IntelligenceReportATFReferralGroupsID').enable();

  }

  filterReferralGroup(fieldDivisionId:string, purposes:string) {
    // Set value for case assist
    if (purposes != null && purposes.toLowerCase() == 'ad50aab0-767f-4b44-bcdf-5b04435a287d') {
      this.filteredReferralGroupsForCaseAssist = [];
      for (let i = 0; i < this.referralGroups.length; i++) {
        let group = this.referralGroups[i];
        if (group.FieldDivisionID.toLowerCase().indexOf(fieldDivisionId.toLowerCase()) == 0) {
          this.filteredReferralGroupsForCaseAssist.push(group);
        }
      }
    }

    // Set value for referral
    if (purposes != null && purposes.toLowerCase() == '044e5fbd-ecd4-49d4-a4ee-61ff70c7855b') {
      this.filteredReferralGroupsForReferral = [];
      for (let i = 0; i < this.referralGroups.length; i++) {
        let group = this.referralGroups[i];
        if (group.FieldDivisionID.toLowerCase().indexOf(fieldDivisionId.toLowerCase()) == 0) {
          this.filteredReferralGroupsForReferral.push(group);
        }
      }
    }
  }

  onChangeFD(event:Event, cgicCode:string) {
    if (!cgicCode) {
      this.cgicPersonnel = [];
      return;
    }
    this.infoReportService.getCGICPersonnelIncludeAgency(cgicCode).pipe(takeUntil(this._unsubscribe$)).subscribe(
      (personnelForCGIC: ServerModels.CGICPersonnelViewModel[]) => {
        this.cgicPersonnel = personnelForCGIC.map(p => { return { label: `${p.LastName}, ${p.FirstName} (${p.Email})`, value: p } });
      });
  }

  onSubmit() {
    if (this.trackingDataForm.valid) {
      ;
      const formData = this.trackingDataForm.getRawValue();
      let selectedReportTypes:any[] = [];
      let selectedPurposes:any[] = [];
      let selectedCgicPersonnel:any[] = [];

      formData.ENESS_XREF_IRsTypes.forEach((x:any) => {
        if (x.chbx) {
          selectedReportTypes.push({
            IRTypeID: x.IRTypeID,
            IntelligenceReportID: x.IntelligenceReportID,
            TypeID: x.TypeID,
            OtherText: x.OtherText
          });
        }
      });
      formData.ENESS_XREF_IRsPurposes.forEach((x:any) => {
        if (x.chbx) {
          let purpose = this.purposes.find(p => p.PurposeID == x.PurposeID).Purpose;
          if (purpose == 'External LE Case Assist/NIBIN Notification') {
            selectedPurposes.push({
              IRPurposeID: x.IRPurposeID,
              IntelligenceReportID: formData.IntelligenceReportID,
              PurposeID: x.PurposeID
            })
          } else {
            selectedPurposes.push({
              IRPurposeID: x.IRPurposeID,
              IntelligenceReportID: x.IntelligenceReportID,
              PurposeID: x.PurposeID,
              IRPurposeFieldDivisionID: x.IRPurposeFieldDivisionID,
              ENESS_XREF_IRsPurposesFieldDivisions: {
                IRPurposeFieldDivisionID: x.IRPurposeFieldDivisionID,
                FieldDivisionID: x.ENESS_XREF_IRsPurposesFieldDivisions.FieldDivisionID,
                IntelligenceReportATFReferralGroupsID: x.ENESS_XREF_IRsPurposesFieldDivisions.IntelligenceReportATFReferralGroupsID,
                NFORCECaseNumber: x.ENESS_XREF_IRsPurposesFieldDivisions.NFORCECaseNumber
              }
            });
          }
        }
      });
      formData.ENESS_XREF_IRsAssignedCGICPersonnel.forEach((x:any) => {
        selectedCgicPersonnel.push({
          IRAssignedCGICPersonnelID: x.IRAssignedCGICPersonnelID,
          IntelligenceReportID: x.IntelligenceReportID,
          UserID: x.UserID,
          IsPrimary: x.IsPrimary
        });
      });

      this.trackingData = {
        formData: {
          Title: formData.Title,
          CGICCode: formData.CGICCode,
          PriorityID: formData.PriorityID,
          StatusID: formData.StatusID,
          ENESS_XREF_IRsPurposes: selectedPurposes,
          ENESS_XREF_IRsTypes: selectedReportTypes,
          ENESS_XREF_IRsAssignedCGICPersonnel: selectedCgicPersonnel,
        },
        tab: 'trackingData'
      };

      this.tdSubmitted.emit(this.trackingData);
    }
  }

  public getENESS_XREF_IRsPurposesFormGroup(idx: number): FormGroup {
    return this.ENESS_XREF_IRsPurposes.at(idx) as FormGroup;
  }

  public getENESS_XREF_IRsTypes(idx: number): FormGroup {
    return this.ENESS_XREF_IRsTypes.at(idx) as FormGroup;
  }

  public getENESS_XREF_IRsPurposesFieldDivisionsFormGroup(idx: number): FormGroup {
    return (<FormGroup>this.getENESS_XREF_IRsPurposesFormGroup(idx)).get('ENESS_XREF_IRsPurposesFieldDivisions') as FormGroup;
  }
}
